import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Layers from '@nerdwallet/react-icon/gizmo/Layers';
import Write from '@nerdwallet/react-icon/gizmo/Write';
import Trash from '@nerdwallet/react-icon/gizmo/Trash';
import nds from '@nerdwallet/base-styles/classes';
import { Type } from '@nerdwallet/currency';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import includes from 'lodash/includes';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';

import {
  selectComponent,
  deleteComponent,
  closeEditor,
  openEditor,
  toggleBadge,
  alwaysRenderInBuilder,
  startSorting,
  stopSorting,
} from '../builder/actions';
import { componentLoadOverrides } from '../../lib/outline-manipulation';
import * as componentLibrary from '../../lib/component-lib';
import { getQueryParams } from '../../lib/query-param-helpers';

import Dropzone from './dropzone';

import './editor-control.less';

// Components that should not move their badge when selected.
const aboveBlacklist = {
  Page: true,
  GlobalNav: true,
};

const nonSortableComponents = new Set([
  'Page',
  'GlobalNav',
  'QueryParamSection',
]);

const childrenToRender = (
  children,
  node,
  outlineLength,
  history,
  watchQueryParams,
  dispatch,
  componentsToAlwaysRenderInBuilder
) => {
  // if no function is defined to check whether we should load the component, we should always load it
  const shouldLoad = get(componentLoadOverrides, node.type, () => true);

  // if we're not on a large page, or we're already filtering by query params, or we should always render this component, render it
  if (
    outlineLength < 40 ||
    watchQueryParams ||
    includes(componentsToAlwaysRenderInBuilder, node.id) ||
    shouldLoad(node, getQueryParams(history))
  ) {
    return children;
  }

  // if the component defines its own JSX to render when it doesn't show in the builder, display that instead of the default
  const alternativeNode = get(
    componentLibrary,
    `${node.type}.alternativeBuilderDisplay`
  );
  const childToRender = alternativeNode ? (
    alternativeNode(dispatch, node)
  ) : (
    <Type
      size={2}
      className={nds('padding-all-4', 'text-align-center')}
      onClick={() => dispatch(alwaysRenderInBuilder(node.id))}
    >
      This component is hidden because it wouldn&apos;t be displaying outside of
      the landing page builder. Click to load me!
    </Type>
  );

  return childToRender;
};

const EditorControl = ({
  children,
  dispatch,
  selected,
  node,
  badgeOpen,
  sortingComponentId,
  outlineLength,
  history,
  watchQueryParams,
  componentsToAlwaysRenderInBuilder,
}) => {
  const baseButton = {
    size: 'small',
  };

  const isSelected = selected === node.id;
  const nodeType = node.type.toLowerCase();

  const editProps = {
    ...baseButton,
    className: 'editor-control__button editor-control__edit',
    onClick: () => {
      // select component
      dispatch(selectComponent(node.id));

      if (isSelected) {
        dispatch(closeEditor);
      } else {
        // make sure the editor is open
        dispatch(openEditor);
      }
    },
  };

  const deleteProps = {
    ...baseButton,
    className: 'editor-control__button editor-control__delete',
    onClick() {
      dispatch(deleteComponent(node.id));
    },
  };

  const componentName = node.type;

  const toggleTitle = `${
    badgeOpen ? 'Close' : 'Open'
  } ${componentName} details.`;

  const shouldBeAboveComponent = isSelected && !aboveBlacklist[componentName];

  const isSortable = !nonSortableComponents.has(node.type);

  const moveProps = isSortable
    ? {
        title: 'Move this component',
        draggable: true,
        onDragStart(evt) {
          /* eslint-disable no-param-reassign */
          evt.dataTransfer.effectAllowed = 'move';
          /* eslint-enable no-param-reassign */

          setTimeout(() => dispatch(startSorting(node.id)), 100);
        },
        onDragEnd() {
          // send a deferred update in case the user drops the component
          // outside a valid dropzone
          setTimeout(() => dispatch(stopSorting), 100);
        },
      }
    : {};

  return (
    <div
      className={classNames({
        'editor-control': true,
        'editor-control__selected': isSelected,
        'editor-control__reorder-moving': sortingComponentId === node.id,
      })}
    >
      <div
        className={`editor-control__type editor-control__type__${nodeType}`}
        style={{
          opacity: badgeOpen ? 1 : 0.4,
          marginTop: shouldBeAboveComponent ? -35 : 0,
        }}
        {...moveProps}
      >
        <Layers
          className="editor-control__logo editor-control__toggle"
          onClick={() => dispatch(toggleBadge(node.id))}
          title={toggleTitle}
        />
        <span
          className="editor-control__details"
          style={{
            display: badgeOpen ? 'inline-block' : 'none',
          }}
        >
          <strong>{componentName}</strong>
          <a {...editProps}>
            <Write />
          </a>
          {node.type !== 'Page' && isSelected ? (
            <React.Fragment>
              <a {...deleteProps}>
                <Trash />
              </a>
            </React.Fragment>
          ) : null}
        </span>
      </div>

      {sortingComponentId !== node.id ? (
        <Dropzone
          targetId={node.id}
          name={componentName}
          sortingComponentId={sortingComponentId}
          dispatch={dispatch}
        />
      ) : null}

      {childrenToRender(
        children,
        node,
        outlineLength,
        history,
        watchQueryParams,
        dispatch,
        componentsToAlwaysRenderInBuilder
      )}
    </div>
  );
};

EditorControl.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element]),
  dispatch: PropTypes.func.isRequired,
  selected: PropTypes.string,
  node: PropTypes.object,
  outlineLength: PropTypes.number,
  badgeOpen: PropTypes.bool,
  sortingComponentId: PropTypes.string,
  history: PropTypes.object,
  watchQueryParams: PropTypes.bool,
  componentsToAlwaysRenderInBuilder: PropTypes.arrayOf(PropTypes.string),
};

EditorControl.displayName = 'EditorControl';

const isValidSibling = (reorderingId, targetId, flatOutline) => {
  const componentType = get(flatOutline, [targetId, 'type']);

  if (nonSortableComponents.has(componentType)) {
    return '';
  }

  let foundId = targetId;

  while (foundId) {
    foundId = get(flatOutline, [foundId, 'parentId']);

    if (foundId === reorderingId) {
      return '';
    }
  }

  return true;
};

const getSortingComponentId = (state, props) => {
  const sortingComponentId = get(state, 'builder.sorting.componentId');
  const targetId = props.node.id;

  return (
    sortingComponentId &&
    isValidSibling(
      sortingComponentId,
      targetId,
      get(state, 'builder.flatOutline')
    ) &&
    sortingComponentId
  );
};

export default compose(
  withRouter,
  connect(
    createSelector(
      property('builder.selected'),
      property('builder.flatOutline'),
      property('builder.watchQueryParams'),
      property('builder.alwaysRenderInBuilder'),
      getSortingComponentId,
      (state, props) => property(['builder', 'badges', props.node.id])(state),
      (
        selected,
        flatOutline,
        watchQueryParams,
        componentsToAlwaysRenderInBuilder,
        sortingComponentId,
        badgeOpen
      ) => ({
        selected,
        outlineLength: Object.keys(flatOutline).length,
        watchQueryParams,
        componentsToAlwaysRenderInBuilder,
        sortingComponentId,
        badgeOpen,
      })
    )
  )
)(EditorControl);
